// src/pages/mikrotik/PPPoEServersPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  Edit2,
  EthernetPort,
  Filter,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  Router as RouterIcon,
  Save,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";
import type { RouterInterfaces } from "../../types/interface";
import type {
  PPPoEServer,
  PPPoEServerForm,
  RouterPPPoEServers,
} from "../../types/pppoe_server";

type RouterId = string | number;

const initialForm: PPPoEServerForm = {
  service_name: "",
  interface: "",
  default_profile: "",
  max_mtu: "auto",
  max_mru: "auto",
  authentication: "pap,chap,mschap1,mschap2",
  max_sessions: "unlimited",
  one_session_per_host: false,
  accept_untagged: true,
  comment: "",
};

interface FlattenedServer extends PPPoEServer {
  routerName: string;
  routerId: RouterId;
  serviceName: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const getField = (obj: any, ...keys: string[]) => {
  for (const key of keys) {
    const value = obj?.[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }

  return "";
};

const toBool = (value: unknown) => {
  if (typeof value === "boolean") return value;

  const text = String(value ?? "")
    .toLowerCase()
    .trim();

  return text === "true" || text === "yes" || text === "1";
};

const getServerName = (server: PPPoEServer): string =>
  getField(server, "service_name", "service-name", "name");

const getDefaultProfile = (server: PPPoEServer): string =>
  getField(server, "default_profile", "default-profile");

const getMaxMtu = (server: PPPoEServer): string =>
  getField(server, "max_mtu", "max-mtu") || "auto";

const getMaxMru = (server: PPPoEServer): string =>
  getField(server, "max_mru", "max-mru") || "auto";

const getAuthentication = (server: PPPoEServer): string =>
  getField(server, "authentication") || "pap,chap,mschap1,mschap2";

const getMaxSessions = (server: PPPoEServer): string =>
  getField(server, "max_sessions", "max-sessions") || "unlimited";

const getOneSessionPerHost = (server: PPPoEServer): boolean =>
  toBool(
    (server as any).one_session_per_host ??
      (server as any)["one-session-per-host"],
  );

const getAcceptUntagged = (server: PPPoEServer): boolean =>
  toBool((server as any).accept_untagged ?? (server as any)["accept-untagged"]);

const isDisabled = (server: PPPoEServer): boolean =>
  toBool((server as any).disabled);

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading PPPoE servers…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while PPPoE server configuration is being prepared.
          </p>

          <div className="relative mt-6 flex justify-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-teal-500 delay-150" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500 delay-300" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  if (disabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
        <XCircle className="h-3 w-3" />
        Disabled
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
      <CheckCircle2 className="h-3 w-3" />
      Enabled
    </span>
  );
};

const BoolBadge: React.FC<{
  active: boolean;
  labelOn: string;
  labelOff: string;
}> = ({ active, labelOn, labelOff }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
      active
        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
        : "border-slate-300 bg-slate-200 text-slate-600"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-slate-500"
      }`}
    />
    {active ? labelOn : labelOff}
  </span>
);

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 9 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-36"
              : index === 3
                ? "w-32"
                : index === 8
                  ? "w-28"
                  : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-40 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const PPPoEServersPage: React.FC = () => {
  const { showToast } = useToast();

  const [routerServers, setRouterServers] = useState<RouterPPPoEServers[]>([]);
  const [routerInterfaces, setRouterInterfaces] = useState<RouterInterfaces[]>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedRouterId, setSelectedRouterId] = useState<string>("all");

  const [form, setForm] = useState<PPPoEServerForm>(initialForm);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [formRouterId, setFormRouterId] = useState<RouterId | "">("");

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    routerId: RouterId;
    name: string;
  } | null>(null);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const showError = (msg: string) => {
    setError(msg);
    setSuccess(null);
  };

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setError(null);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      resetMessages();

      const [serversData, interfacesData] = await Promise.all([
        fetchApp<RouterPPPoEServers[]>("/pppoe-server/"),
        fetchApp<RouterInterfaces[]>("/interface/"),
      ]);

      const normalizedServers = Array.isArray(serversData) ? serversData : [];

      setRouterServers(normalizedServers);
      setRouterInterfaces(Array.isArray(interfacesData) ? interfacesData : []);

      setFormRouterId((current) => {
        if (
          current &&
          normalizedServers.some(
            (router: any) => String(router.router_id) === String(current),
          )
        ) {
          return current;
        }

        return (normalizedServers[0] as any)?.router_id ?? "";
      });
    } catch (err: any) {
      const msg = err?.message || "Failed to load PPPoE servers.";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allServers = useMemo<FlattenedServer[]>(() => {
    return routerServers.flatMap((router: any) =>
      (router.servers || []).map((server: PPPoEServer) => ({
        ...server,
        routerName: router.router_name,
        routerId: router.router_id,
        serviceName: getServerName(server),
      })),
    );
  }, [routerServers]);

  const filteredServers = useMemo(() => {
    const term = search.toLowerCase().trim();

    return allServers.filter((item) => {
      if (
        selectedRouterId !== "all" &&
        String(item.routerId) !== selectedRouterId
      ) {
        return false;
      }

      if (!term) return true;

      return [
        item.serviceName,
        item.interface,
        item.routerName,
        getDefaultProfile(item),
        getAuthentication(item),
        item.comment || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [allServers, search, selectedRouterId]);

  const stats = useMemo(() => {
    const totalRouters = routerServers.length;
    const total = filteredServers.length;
    const enabled = filteredServers.filter(
      (server) => !isDisabled(server),
    ).length;
    const disabled = total - enabled;
    const oneSession = filteredServers.filter(getOneSessionPerHost).length;
    const acceptUntagged = filteredServers.filter(getAcceptUntagged).length;

    return {
      totalRouters,
      total,
      enabled,
      disabled,
      oneSession,
      acceptUntagged,
    };
  }, [routerServers.length, filteredServers]);

  const interfaceOptions = useMemo(() => {
    if (!formRouterId) return [];

    const router = routerInterfaces.find(
      (r: any) => String(r.router_id) === String(formRouterId),
    ) as any;

    if (!router) return [];

    return Array.from(
      new Set(
        (router.interfaces || [])
          .map((item: any) => item?.name)
          .filter(Boolean),
      ),
    ) as string[];
  }, [routerInterfaces, formRouterId]);

  const profileOptions = useMemo(() => {
    if (!formRouterId) return [];

    const router = routerServers.find(
      (r: any) => String(r.router_id) === String(formRouterId),
    ) as any;

    if (!router) return [];

    return Array.from(
      new Set(
        (router.servers || [])
          .map((server: PPPoEServer) => getDefaultProfile(server))
          .filter(Boolean),
      ),
    ) as string[];
  }, [routerServers, formRouterId]);

  const selectedFormRouter = useMemo(() => {
    return (
      routerServers.find(
        (router: any) => String(router.router_id) === String(formRouterId),
      ) || null
    );
  }, [routerServers, formRouterId]);

  const openCreateModal = () => {
    resetMessages();
    setEditingName(null);
    setForm(initialForm);

    if (!formRouterId && (routerServers[0] as any)?.router_id) {
      setFormRouterId((routerServers[0] as any).router_id);
    }

    setIsModalOpen(true);
  };

  const openEditModal = (server: FlattenedServer) => {
    resetMessages();

    setFormRouterId(server.routerId);
    setEditingName(server.serviceName);

    setForm({
      service_name: server.serviceName,
      interface: server.interface || "",
      default_profile: getDefaultProfile(server),
      max_mtu: getMaxMtu(server),
      max_mru: getMaxMru(server),
      authentication: getAuthentication(server),
      max_sessions: getMaxSessions(server),
      one_session_per_host: getOneSessionPerHost(server),
      accept_untagged: getAcceptUntagged(server),
      comment: server.comment || "",
    });

    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setIsModalOpen(false);
    setEditingName(null);
    setForm(initialForm);
  };

  const handleSaveServer = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();

    if (!formRouterId) {
      const msg = "Please select a router first.";
      showError(msg);
      showToast(msg, "error");
      return;
    }

    if (!form.service_name.trim() || !form.interface.trim()) {
      const msg = "Service name and interface are required.";
      showError(msg);
      showToast(msg, "error");
      return;
    }

    try {
      setSaving(true);

      const body = {
        service_name: form.service_name.trim(),
        interface: form.interface.trim(),
        default_profile: form.default_profile.trim(),
        max_mtu: form.max_mtu || "auto",
        max_mru: form.max_mru || "auto",
        authentication: form.authentication || "pap,chap,mschap1,mschap2",
        max_sessions: form.max_sessions || "unlimited",
        one_session_per_host: form.one_session_per_host,
        accept_untagged: form.accept_untagged,
        comment: form.comment || "",
      };

      const method = editingName ? "PUT" : "POST";
      const url = editingName
        ? `/pppoe-server/${encodeURIComponent(editingName)}?router_id=${formRouterId}`
        : `/pppoe-server?router_id=${formRouterId}`;

      const res = await fetchApp<any>(url, {
        method,
        body: JSON.stringify(body),
      });

      if (res && typeof res === "object" && "error" in res) {
        throw new Error(res.error || "Failed to save PPPoE server.");
      }

      const msg = editingName
        ? "PPPoE server updated successfully."
        : "PPPoE server created successfully.";

      showSuccess(msg);
      showToast(msg, "success");

      setIsModalOpen(false);
      setEditingName(null);
      setForm(initialForm);

      await fetchData();
    } catch (err: any) {
      const msg = err?.message || "Failed to save PPPoE server.";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleServer = async (server: FlattenedServer) => {
    resetMessages();

    try {
      const nextDisabled = !isDisabled(server);

      await fetchApp(
        `/pppoe-server/${encodeURIComponent(server.serviceName)}?router_id=${server.routerId}`,
        {
          method: "PUT",
          body: JSON.stringify({ disabled: nextDisabled }),
        },
      );

      const msg = nextDisabled
        ? "PPPoE server disabled successfully."
        : "PPPoE server enabled successfully.";

      showSuccess(msg);
      showToast(msg, "success");

      await fetchData();
    } catch (err: any) {
      const msg = err?.message || "Failed to update PPPoE server state.";
      showError(msg);
      showToast(msg, "error");
    }
  };

  const askDeleteServer = (routerId: RouterId, name: string) => {
    setPendingDelete({ routerId, name });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      setConfirmLoading(true);
      resetMessages();

      const res = await fetchApp<any>(
        `/pppoe-server/${encodeURIComponent(pendingDelete.name)}?router_id=${
          pendingDelete.routerId
        }`,
        { method: "DELETE" },
      );

      if (res && typeof res === "object" && "error" in res) {
        throw new Error(res.error || "Failed to delete PPPoE server.");
      }

      const msg = "PPPoE server deleted successfully.";
      showSuccess(msg);
      showToast(msg, "success");

      await fetchData();
    } catch (err: any) {
      const msg = err?.message || "Failed to delete PPPoE server.";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedRouterId("all");
  };

  if (loading && routerServers.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  MikroTik PPPoE Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  PPPoE Servers
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Manage PPPoE server services, interfaces, authentication
                  methods, profiles, and access rules across your routers.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={fetchData}
                  disabled={loading || saving}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {loading ? "Refreshing…" : "Refresh"}
                </button>

                <button
                  type="button"
                  onClick={openCreateModal}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  New Server
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && routerServers.length > 0 && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing PPPoE servers…
          </div>
        )}

        {(error || success) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-3xl border px-4 py-3.5 text-[13px] font-bold shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700 shadow-rose-200/40"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-200/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {error ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              )}

              <p>{error || success}</p>
            </div>

            <button
              type="button"
              onClick={() => {
                setError(null);
                setSuccess(null);
              }}
              className={`rounded-xl p-1.5 transition-colors ${
                error
                  ? "text-rose-600 hover:bg-rose-100"
                  : "text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Routers"
            value={stats.totalRouters}
            sub="Routers checked"
            icon={<RouterIcon className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Servers"
            value={stats.total}
            sub="Filtered PPPoE services"
            icon={<Server className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Enabled"
            value={stats.enabled}
            sub="Active PPPoE servers"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Disabled"
            value={stats.disabled}
            sub="Currently disabled"
            icon={<XCircle className="h-5 w-5" />}
            tone={stats.disabled > 0 ? "amber" : "slate"}
          />

          <StatCard
            label="One Session"
            value={stats.oneSession}
            sub="One session per host"
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="emerald"
          />
        </div>

        {/* Main Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Settings className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  PPPoE Server List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Showing {filteredServers.length} of {allServers.length}{" "}
                  servers
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <div className="relative w-full lg:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search service, interface, profile..."
                  className={`${inputCls} pl-10`}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative w-full lg:w-72">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={selectedRouterId}
                  onChange={(e) => setSelectedRouterId(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="all">All Routers</option>

                  {routerServers.map((router: any) => (
                    <option
                      key={String(router.router_id)}
                      value={String(router.router_id)}
                    >
                      {router.router_name}
                    </option>
                  ))}
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {(search || selectedRouterId !== "all") && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Service</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 font-black">Interface</th>
                  <th className="px-5 py-3.5 font-black">Profile</th>
                  <th className="px-5 py-3.5 font-black">MTU / MRU</th>
                  <th className="px-5 py-3.5 font-black">Auth</th>
                  <th className="px-5 py-3.5 font-black">Rules</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredServers.length > 0 ? (
                  filteredServers.map((server) => {
                    const disabled = isDisabled(server);

                    return (
                      <tr
                        key={`${server.routerId}-${server.serviceName}`}
                        className="transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                              <Server className="h-4 w-4" />
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-black text-slate-900">
                                {server.serviceName || "Unnamed service"}
                              </p>

                              <p className="mt-0.5 truncate text-[11px] font-medium text-slate-500">
                                {server.comment || "No comment"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                            <RouterIcon className="h-3 w-3" />
                            {server.routerName}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 font-mono text-[12px] font-bold text-slate-700">
                            <EthernetPort className="h-3.5 w-3.5 text-slate-400" />
                            {server.interface || "—"}
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          {getDefaultProfile(server) ? (
                            <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                              {getDefaultProfile(server)}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>

                        <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-600">
                          {getMaxMtu(server)} / {getMaxMru(server)}
                        </td>

                        <td className="max-w-[220px] px-5 py-4">
                          <p
                            className="truncate text-[12px] font-bold text-slate-600"
                            title={getAuthentication(server)}
                          >
                            {getAuthentication(server)}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            <BoolBadge
                              active={getOneSessionPerHost(server)}
                              labelOn="One session"
                              labelOff="Multi-session"
                            />
                            <BoolBadge
                              active={getAcceptUntagged(server)}
                              labelOn="Untagged"
                              labelOff="Tagged only"
                            />
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <StatusBadge disabled={disabled} />
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleToggleServer(server)}
                              className={`${btnBase} h-9 border px-3 ${
                                disabled
                                  ? "border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                  : "border-amber-200 bg-amber-100 text-amber-800 hover:bg-amber-200"
                              }`}
                            >
                              <Power className="h-3.5 w-3.5" />
                              {disabled ? "Enable" : "Disable"}
                            </button>

                            <button
                              type="button"
                              onClick={() => openEditModal(server)}
                              className={`${btnBase} h-9 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              Edit
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                askDeleteServer(
                                  server.routerId,
                                  server.serviceName,
                                )
                              }
                              className={`${btnBase} h-9 border border-rose-200 bg-rose-100 px-3 text-rose-700 hover:bg-rose-200`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Server className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No PPPoE servers found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        No PPPoE server records match your current filters.
                      </p>

                      {(search || selectedRouterId !== "all") && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredServers.length > 0 ? (
              filteredServers.map((server) => {
                const disabled = isDisabled(server);

                return (
                  <div
                    key={`${server.routerId}-${server.serviceName}`}
                    className="px-4 py-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-black text-slate-900">
                          {server.serviceName || "Unnamed service"}
                        </p>

                        <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                          {server.comment || "No comment"}
                        </p>
                      </div>

                      <StatusBadge disabled={disabled} />
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                        <RouterIcon className="h-3 w-3" />
                        {server.routerName}
                      </span>

                      {getDefaultProfile(server) && (
                        <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                          {getDefaultProfile(server)}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Interface
                        </p>

                        <p className="mt-1 flex items-center gap-1.5 font-mono text-[12.5px] font-black text-slate-900">
                          <EthernetPort className="h-3.5 w-3.5 text-slate-400" />
                          {server.interface || "—"}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                          MTU / MRU
                        </p>

                        <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                          {getMaxMtu(server)} / {getMaxMru(server)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Authentication
                      </p>

                      <p className="mt-1 break-words text-[12px] font-bold text-slate-700">
                        {getAuthentication(server)}
                      </p>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <BoolBadge
                        active={getOneSessionPerHost(server)}
                        labelOn="One session"
                        labelOff="Multi-session"
                      />
                      <BoolBadge
                        active={getAcceptUntagged(server)}
                        labelOn="Untagged"
                        labelOff="Tagged only"
                      />
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <button
                        type="button"
                        onClick={() => handleToggleServer(server)}
                        className={`${btnBase} h-10 border px-3 ${
                          disabled
                            ? "border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                            : "border-amber-200 bg-amber-100 text-amber-800 hover:bg-amber-200"
                        }`}
                      >
                        <Power className="h-4 w-4" />
                        {disabled ? "Enable" : "Disable"}
                      </button>

                      <button
                        type="button"
                        onClick={() => openEditModal(server)}
                        className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          askDeleteServer(server.routerId, server.serviceName)
                        }
                        className={`${btnBase} h-10 border border-rose-200 bg-rose-100 px-3 text-rose-700 hover:bg-rose-200`}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Server className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No PPPoE servers found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  No PPPoE server records match your current filters.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredServers.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {allServers.length}
              </span>{" "}
              PPPoE servers
            </p>

            {(search || selectedRouterId !== "all") && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  {editingName ? (
                    <Edit2 className="h-5 w-5" />
                  ) : (
                    <Plus className="h-5 w-5" />
                  )}
                </div>

                <div>
                  <p className="text-[15px] font-black text-slate-900">
                    {editingName ? "Edit PPPoE Server" : "Create PPPoE Server"}
                  </p>

                  <p className="mt-1 text-[12.5px] font-medium leading-relaxed text-slate-500">
                    Configure service name, interface, profile, authentication,
                    MTU/MRU and session rules.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form
              onSubmit={handleSaveServer}
              className="max-h-[calc(92vh-88px)] overflow-y-auto p-5"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Router
                  </label>

                  <select
                    value={String(formRouterId)}
                    onChange={(e) => setFormRouterId(e.target.value)}
                    disabled={saving || Boolean(editingName)}
                    className={`${inputCls} cursor-pointer`}
                  >
                    <option value="">Select router...</option>

                    {routerServers.map((router: any) => (
                      <option
                        key={String(router.router_id)}
                        value={String(router.router_id)}
                      >
                        {router.router_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Service Name
                  </label>

                  <input
                    value={form.service_name}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        service_name: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="e.g. pppoe-server1"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Interface
                  </label>

                  <input
                    list="pppoe-interface-options"
                    value={form.interface}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        interface: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="Select or type interface"
                    className={inputCls}
                  />

                  <datalist id="pppoe-interface-options">
                    {interfaceOptions.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Default Profile
                  </label>

                  <input
                    list="pppoe-profile-options"
                    value={form.default_profile}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        default_profile: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="e.g. default"
                    className={inputCls}
                  />

                  <datalist id="pppoe-profile-options">
                    {profileOptions.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Max MTU
                  </label>

                  <input
                    value={form.max_mtu}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        max_mtu: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="auto or 1480"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Max MRU
                  </label>

                  <input
                    value={form.max_mru}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        max_mru: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="auto or 1480"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Max Sessions
                  </label>

                  <input
                    value={form.max_sessions}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        max_sessions: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="unlimited"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Authentication
                  </label>

                  <input
                    value={form.authentication}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        authentication: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="pap,chap,mschap1,mschap2"
                    className={inputCls}
                  />
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={form.one_session_per_host}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          one_session_per_host: e.target.checked,
                        }))
                      }
                      disabled={saving}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />

                    <span>
                      <span className="block text-[13px] font-black text-slate-900">
                        One session per host
                      </span>
                      <span className="mt-0.5 block text-[12px] font-medium text-slate-500">
                        Prevent the same host from opening multiple PPPoE
                        sessions.
                      </span>
                    </span>
                  </label>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={form.accept_untagged}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          accept_untagged: e.target.checked,
                        }))
                      }
                      disabled={saving}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />

                    <span>
                      <span className="block text-[13px] font-black text-slate-900">
                        Accept untagged
                      </span>
                      <span className="mt-0.5 block text-[12px] font-medium text-slate-500">
                        Allow untagged PPPoE discovery traffic on this service.
                      </span>
                    </span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Comment
                  </label>

                  <textarea
                    value={form.comment}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        comment: e.target.value,
                      }))
                    }
                    disabled={saving}
                    rows={3}
                    placeholder="Optional comment..."
                    className={textareaCls}
                  />
                </div>
              </div>

              {selectedFormRouter && (
                <p className="mt-4 text-[12px] font-medium text-slate-500">
                  Target router:{" "}
                  <span className="font-black text-slate-800">
                    {(selectedFormRouter as any).router_name}
                  </span>
                </p>
              )}

              <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving
                    ? "Saving…"
                    : editingName
                      ? "Update Server"
                      : "Create Server"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        title="Delete PPPoE server?"
        description={
          pendingDelete
            ? `The PPPoE server “${pendingDelete.name}” will be removed from the selected router. This action cannot be undone.`
            : "This PPPoE server will be removed. This action cannot be undone."
        }
        confirmLabel="Delete server"
        cancelLabel="Cancel"
        variant="danger"
        onCancel={() => {
          if (confirmLoading) return;
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default PPPoEServersPage;
